import {useState,useEffect, useRef, SyntheticEvent, KeyboardEvent as ReactKeyboardEvent} from "react";
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';
import { useOutletContext } from "react-router-dom";


// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
// import required modules
import { Navigation} from 'swiper/modules';
import EtcCtxtLstApi from "Apis/EtcCtxtLstApi";

const DEFAULT_CLIP_URL = "https://bb-cdn.gameking.com/web/mainpage/gameclip.mp4";

const getYoutubeVideoId = (url: string): string | null => {
    if (!url) return null;
    const trimmedUrl = url.trim();
    const patterns = [
        /youtu\.be\/([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of patterns) {
        const match = trimmedUrl.match(pattern);
        if (match?.[1]) {
            return match[1];
        }
    }

    try {
        const parsed = new URL(trimmedUrl);
        const host = parsed.hostname.toLowerCase();
        if (host.includes("youtube.com") || host.includes("youtu.be")) {
            const v = parsed.searchParams.get("v");
            if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) {
                return v;
            }
        }
    } catch (_err) {
        // Ignore URL parse errors and treat it as a non-youtube URL.
    }

    return null;
};

const isYoutubeUrl = (url: string): boolean => getYoutubeVideoId(url) !== null;

const buildYoutubeEmbedUrl = (url: string, autoplay = false): string => {
    const videoId = getYoutubeVideoId(url);
    if (!videoId) return "";
    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1&autoplay=${autoplay ? 1 : 0}`;
};

const getYoutubeThumbnailCandidates = (url: string): string[] => {
    const videoId = getYoutubeVideoId(url);
    if (!videoId) return [];
    return [
        `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
        `https://i.ytimg.com/vi/${videoId}/hq720.jpg`,
        `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`,
        `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`,
        `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
    ];
};

const YoutubeThumbnail = ({url, title}: {url: string; title: string}) => {
    const candidates = getYoutubeThumbnailCandidates(url);
    const [thumbIndex, setThumbIndex] = useState(0);
    const currentSrc = candidates[thumbIndex] || "";

    if (!currentSrc) return null;

    return (
        <img
            src={currentSrc}
            alt={`${title || "GAME CLIP"} 썸네일`}
            className="mainGuideYoutubeThumb"
            loading="lazy"
            onError={() => {
                setThumbIndex((prev) => (prev + 1 < candidates.length ? prev + 1 : prev));
            }}
        />
    );
};

interface MainClipItem {
    ctntTitl: string;
    ctntCtxt: string;
    ctntUrl: string;
}

const MainGuideSlideUi = ({gameSvcCd,gameMainPageInfo}:any) => {
    const {gameMainPageData, langData}:any = useOutletContext();
    const [gameMainPageInfoState,setGameMainPageInfoState] = useState<any>();
    const [mainClipList, setMainClipList] = useState<MainClipItem[]>([]);
    const [activeClipIndex, setActiveClipIndex] = useState(0);
    const [videoAspectRatio, setVideoAspectRatio] = useState("16 / 9");
    const [modalIndex, setModalIndex] = useState<number | null>(null);
    const [modalPlayingIndex, setModalPlayingIndex] = useState<number | null>(null);
    const guideVideoRefs = useRef<Array<HTMLVideoElement | null>>([]);
    const modalVideoRefs = useRef<Array<HTMLVideoElement | null>>([]);
    const swiperRef = useRef<SwiperType | null>(null);
    const prevButtonRef = useRef<HTMLButtonElement | null>(null);
    const nextButtonRef = useRef<HTMLButtonElement | null>(null);

    const effectiveClipList: MainClipItem[] = mainClipList.length > 0
        ? mainClipList
        : [{ ctntTitl: 'GAME CLIP', ctntCtxt: '', ctntUrl: DEFAULT_CLIP_URL }];

    const currentModalClip = modalIndex !== null ? effectiveClipList[modalIndex] : null;

    const updateVisibleEdgeSlides = (swiper: SwiperType | null) => {
        if (!swiper?.slides?.length || !swiper.el) return;

        const slides = Array.from(swiper.slides) as HTMLElement[];
        slides.forEach((slideEl) => {
            slideEl.classList.remove('mainGuideEdgeLeft', 'mainGuideEdgeRight', 'mainGuideEdgeCenter');
        });

        const swiperRect = swiper.el.getBoundingClientRect();
        const visibleSlides = slides
            .filter((slideEl) => {
                const slideRect = slideEl.getBoundingClientRect();
                return slideRect.right > swiperRect.left + 1 && slideRect.left < swiperRect.right - 1;
            })
            .sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
        if (visibleSlides.length === 0) return;

        const leftEdgeSlide = visibleSlides[0];
        const rightEdgeSlide = visibleSlides[visibleSlides.length - 1];
        const centerSlide = visibleSlides[Math.floor((visibleSlides.length - 1) / 2)];

        leftEdgeSlide.classList.add('mainGuideEdgeLeft');
        if (rightEdgeSlide !== leftEdgeSlide) {
            rightEdgeSlide.classList.add('mainGuideEdgeRight');
        }
        if (centerSlide) {
            centerSlide.classList.add('mainGuideEdgeCenter');
        }
    };

    const resetVideoToStart = (videoEl: HTMLVideoElement | null) => {
        if (!videoEl) return;
        videoEl.pause();
        try {
            videoEl.currentTime = 0;
        } catch (_err) {
            // Ignore seek failures for not-yet-loaded media.
        }
    };

    const resetInlineVideos = () => {
        guideVideoRefs.current.forEach((videoEl) => {
            resetVideoToStart(videoEl);
        });
    };

    const handleVideoMetadata = (e: SyntheticEvent<HTMLVideoElement>) => {
        const target = e.currentTarget;
        if(target.videoWidth > 0 && target.videoHeight > 0) {
            setVideoAspectRatio(`${target.videoWidth} / ${target.videoHeight}`);
        }
    };

    useEffect(()=>{
        setGameMainPageInfoState(gameMainPageInfo);
        EtcCtxtLstApi("CTNT_KIND_GAMECLIP","CTNT_KIND_GAMECLIP",gameSvcCd)
        .then((resp) => {
            const clipList = resp?.apiBody?.ctntCtxtLst || [];
            setMainClipList(clipList);
            setActiveClipIndex(0);
        });
    },[gameSvcCd])

    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setModalIndex(null);
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                setModalIndex((prev) => {
                    if (prev === null) return prev;
                    const total = effectiveClipList.length;
                    if (total <= 1) return prev;
                    return (prev - 1 + total) % total;
                });
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                setModalIndex((prev) => {
                    if (prev === null) return prev;
                    const total = effectiveClipList.length;
                    if (total <= 1) return prev;
                    return (prev + 1) % total;
                });
            }
        };

        if (modalIndex !== null) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [modalIndex, effectiveClipList.length]);

    const openVideoModal = (index: number) => {
        const target = effectiveClipList[index];
        if (!target?.ctntUrl) return;

        resetInlineVideos();
        setModalIndex(index);
        setModalPlayingIndex(null);
    };

    const closeVideoModal = () => {
        setModalIndex(null);
        setModalPlayingIndex(null);
    };

    const moveModalClip = (direction: -1 | 1) => {
        setModalIndex((prev) => {
            if (prev === null) return prev;
            const total = effectiveClipList.length;
            if (total <= 1) return prev;
            setModalPlayingIndex(null);
            return (prev + direction + total) % total;
        });
    };

    const handleCardKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>, index: number) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openVideoModal(index);
        }
    };

    const handleInlinePlayClick = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
        event.preventDefault();
        event.stopPropagation();
        openVideoModal(index);
    };

    const handleModalPlayClick = () => {
        if (modalIndex === null) return;
        const videoEl = modalVideoRefs.current[modalIndex];
        if (!videoEl) return;

        try {
            videoEl.currentTime = 0;
        } catch (_err) {
            // Ignore seek failures for not-yet-loaded media.
        }

        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
        }
        setModalPlayingIndex(modalIndex);
    };

    useEffect(() => {
        modalVideoRefs.current.forEach((videoEl, index) => {
            if (!videoEl) return;
            if (modalIndex === null || index !== modalIndex) {
                resetVideoToStart(videoEl);
            }
        });
        setModalPlayingIndex(null);
    }, [modalIndex, effectiveClipList.length]);

    useEffect(() => {
        const swiper = swiperRef.current;
        const prevEl = prevButtonRef.current;
        const nextEl = nextButtonRef.current;
        if (!swiper || !prevEl || !nextEl || !swiper.navigation) return;

        if (typeof swiper.params.navigation === 'boolean') {
            swiper.params.navigation = {};
        }

        const nav = swiper.params.navigation as any;
        nav.prevEl = prevEl;
        nav.nextEl = nextEl;

        swiper.navigation.destroy();
        swiper.navigation.init();
        swiper.navigation.update();
        updateVisibleEdgeSlides(swiper);
    }, [mainClipList.length]);

    return(
        <>
            <div className="mainGuideSectionInner">
                <div className="ctntLstTitlArea gamePagectntLstTitlArea ctntLstTitlWrap ">
                    <div className="PNOTTitlWrap ctntLstTitlWrap mainPageCtntTitlWrap">
                        <p>GAME CLIP</p>
                    </div>
                </div>
                <div className="ctntLstContentsWrap videoWrap mainGuideVideoWrap">


                {mainClipList.length > 0 ? (
                    <div className="mainGuideVideoSliderWrap">
                        <button
                            ref={prevButtonRef}
                            type="button"
                            className="mainGuideNavBtn mainGuideNavPrev"
                            aria-label="Previous slide"
                            onClick={() => swiperRef.current?.slidePrev()}
                        />
                        <Swiper
                            slidesPerView={3}
                            spaceBetween={18}
                            watchOverflow={false}
                            watchSlidesProgress={true}
                            loop={mainClipList.length > 1}
                            modules={[Navigation]}
                            className="mainGuideVideoSwiper"
                            onSwiper={(swiper: SwiperType) => {
                                swiperRef.current = swiper;
                                requestAnimationFrame(() => {
                                    updateVisibleEdgeSlides(swiper);
                                });
                            }}
                            navigation={{
                                prevEl: prevButtonRef.current,
                                nextEl: nextButtonRef.current,
                            }}
                            onBeforeInit={(swiper: SwiperType) => {
                                if (typeof swiper.params.navigation === 'boolean') {
                                    swiper.params.navigation = {};
                                }
                                const nav = swiper.params.navigation as any;
                                nav.prevEl = prevButtonRef.current;
                                nav.nextEl = nextButtonRef.current;
                            }}
                            breakpoints={{
                                0: {
                                    slidesPerView: 1,
                                    spaceBetween: 10,
                                },
                                768: {
                                    slidesPerView: 2,
                                    spaceBetween: 14,
                                },
                                1200: {
                                    slidesPerView: 3,
                                    spaceBetween: 18,
                                },
                            }}
                            onSlideChange={(swiper: SwiperType) => {
                                setActiveClipIndex(swiper.realIndex);
                                resetInlineVideos();
                                updateVisibleEdgeSlides(swiper);
                            }}
                            onTransitionEnd={(swiper: SwiperType) => {
                                updateVisibleEdgeSlides(swiper);
                            }}
                            onProgress={(swiper: SwiperType) => {
                                updateVisibleEdgeSlides(swiper);
                            }}
                            onResize={(swiper: SwiperType) => {
                                updateVisibleEdgeSlides(swiper);
                            }}
                        >
                            {mainClipList.map((clip, index) => (
                                <SwiperSlide key={`${clip.ctntUrl}-${index}`}>
                                    {(() => {
                                        const isYoutubeClip = isYoutubeUrl(clip.ctntUrl);
                                        return (
                                    <div
                                        className={`mainGuideClipCard${index === activeClipIndex ? ' is-active' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => openVideoModal(index)}
                                        onKeyDown={(event) => handleCardKeyDown(event, index)}
                                        aria-label={`${clip.ctntTitl || 'GAME CLIP'} 영상 모달 열기`}
                                    >
                                        <div className="mainGuideVideoFrame" style={{ aspectRatio: videoAspectRatio }}>
                                            <div className="mainGuideMediaClip">
                                                {isYoutubeClip ? (
                                                    <YoutubeThumbnail url={clip.ctntUrl} title={clip.ctntTitl} />
                                                ) : (
                                                    <video
                                                        src={clip.ctntUrl}
                                                        ref={(node) => {
                                                            guideVideoRefs.current[index] = node;
                                                        }}
                                                        loop
                                                        muted
                                                        playsInline
                                                        disablePictureInPicture
                                                        disableRemotePlayback
                                                        controlsList="nodownload noplaybackrate"
                                                        onLoadedMetadata={handleVideoMetadata}
                                                    ></video>
                                                )}
                                                <button type="button" className="mainGuidePlayBtn" onClick={(event) => handleInlinePlayClick(event, index)}>
                                                    PLAY
                                                </button>
                                            </div>
                                            <div className="mainGuideClipText">
                                                <p className="guideCtxtTitle">{clip.ctntTitl}</p>
                                                <p className="guideCtxtText">{clip.ctntCtxt}</p>
                                            </div>
                                            <span className="mainGuideFrameOverlay" aria-hidden="true">
                                                <svg viewBox="0 0 100 100" preserveAspectRatio="none" focusable="false" aria-hidden="true">
                                                    <polygon className="outerFrame" points="4,0 96,0 100,4 100,96 96,100 4,100 0,96 0,4" />
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                        );
                                    })()}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <button
                            ref={nextButtonRef}
                            type="button"
                            className="mainGuideNavBtn mainGuideNavNext"
                            aria-label="Next slide"
                            onClick={() => swiperRef.current?.slideNext()}
                        />
                    </div>
                ) : (
                    <div
                        className="mainGuideVideoFrame"
                        style={{ aspectRatio: videoAspectRatio }}
                        role="button"
                        tabIndex={0}
                        onClick={() => openVideoModal(0)}
                        onKeyDown={(event) => handleCardKeyDown(event, 0)}
                        aria-label="GAME CLIP 영상 모달 열기"
                    >
                        <div className="mainGuideMediaClip">
                            <video
                                src={DEFAULT_CLIP_URL}
                                ref={(node) => {
                                    guideVideoRefs.current[0] = node;
                                }}
                                loop
                                muted
                                playsInline
                                disablePictureInPicture
                                disableRemotePlayback
                                controlsList="nodownload noplaybackrate"
                                onLoadedMetadata={handleVideoMetadata}
                            ></video>
                            <button type="button" className="mainGuidePlayBtn" onClick={(event) => handleInlinePlayClick(event, 0)}>
                                PLAY
                            </button>
                        </div>
                        <span className="mainGuideFrameOverlay" aria-hidden="true">
                            <svg viewBox="0 0 100 100" preserveAspectRatio="none" focusable="false" aria-hidden="true">
                                <polygon className="outerFrame" points="4,0 96,0 100,4 100,96 96,100 4,100 0,96 0,4" />
                                <polygon className="innerFrame" points="7,2 93,2 98,7 98,93 93,98 7,98 2,93 2,7" />
                            </svg>
                        </span>
                    </div>
                )}
                </div>
            </div>
            {currentModalClip && (
                <div className="mainGuideVideoModal" role="dialog" aria-modal="true" aria-label="GAME CLIP 영상 재생" onClick={closeVideoModal}>
                    <div className="mainGuideVideoModalShell">
                        {effectiveClipList.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    className="mainGuideVideoModalNav mainGuideVideoModalPrev"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        moveModalClip(-1);
                                    }}
                                    aria-label="이전 영상"
                                >
                                    ‹
                                </button>
                                <button
                                    type="button"
                                    className="mainGuideVideoModalNav mainGuideVideoModalNext"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        moveModalClip(1);
                                    }}
                                    aria-label="다음 영상"
                                >
                                    ›
                                </button>
                            </>
                        )}
                        <div className="mainGuideVideoModalBody" onClick={(event) => event.stopPropagation()}>
                            <div className="mainGuideVideoModalStage" style={{ aspectRatio: videoAspectRatio }}>
                                {effectiveClipList.map((clip, index) => {
                                    const isActive = index === modalIndex;
                                    const isYoutubeClip = isYoutubeUrl(clip.ctntUrl);
                                    const shouldAutoplayYoutube = false;
                                    if (isYoutubeClip) {
                                        if (!isActive) return null;
                                        return (
                                            <iframe
                                                key={`${clip.ctntUrl}-${index}-${shouldAutoplayYoutube ? "play" : "idle"}`}
                                                src={buildYoutubeEmbedUrl(clip.ctntUrl, shouldAutoplayYoutube)}
                                                className="mainGuideVideoModalYoutube is-active"
                                                title={clip.ctntTitl || "GAME CLIP"}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                allowFullScreen
                                                loading="eager"
                                            />
                                        );
                                    }
                                    return (
                                        <video
                                            key={`${clip.ctntUrl}-${index}`}
                                            src={clip.ctntUrl}
                                            ref={(node) => {
                                                modalVideoRefs.current[index] = node;
                                            }}
                                            controls={false}
                                            playsInline
                                            disablePictureInPicture
                                            disableRemotePlayback
                                            preload="auto"
                                            className={`mainGuideVideoModalPlayer${isActive ? ' is-active' : ''}`}
                                            aria-hidden={!isActive}
                                            onPause={() => {
                                                if (isActive && modalPlayingIndex === index) {
                                                    setModalPlayingIndex(null);
                                                }
                                            }}
                                            onEnded={() => {
                                                if (isActive && modalPlayingIndex === index) {
                                                    setModalPlayingIndex(null);
                                                }
                                            }}
                                        />
                                    );
                                })}
                                {modalIndex !== null && modalPlayingIndex !== modalIndex && !isYoutubeUrl(currentModalClip?.ctntUrl || "") && (
                                    <button type="button" className="mainGuideModalPlayBtn" onClick={handleModalPlayClick}>
                                        PLAY
                                    </button>
                                )}
                            </div>
                            <div className="mainGuideVideoModalText">
                                <p className="mainGuideVideoModalTitle">{currentModalClip.ctntTitl}</p>
                                <p className="mainGuideVideoModalDesc">{currentModalClip.ctntCtxt}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default MainGuideSlideUi;